/**
 * Page liste projets — `/projects` (refonte 9.P.3.B parité Companies R1).
 *
 * Pattern UX commun (R1+R2+9.P.3.A) :
 *   - titre dans le flux ; création via le CTA global
 *   - filters bar (search + status select) fixe sur desktop
 *   - table moderne `divide-slate-200` desktop ≥ md / cards stack < md
 *   - status badges cohérents (mapping enum → couleur)
 *   - states distincts : loading skeleton / empty actionable / no-match / error retry
 *   - max-w-screen-2xl (anticipation tableaux comparaison multi-assureurs)
 */

import type { ProjectRow } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { SearchInput } from "../../components/SearchInput";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useProjects } from "../../lib/queries/projects";
import { PreparationCabinetGate } from "../quote-requests/_parts/PreparationCabinetGate";

import {
  EtatOperationnelAction,
  EtatOperationnelBadge,
  EtatOperationnelDetail,
} from "./_parts/EtatOperationnel";
import { ProjectStatusBadge } from "./_parts/ProjectStatusBadge";

const STATUSES: { key: ProjectRow["status"] | "all" }[] = [
  { key: "all" },
  { key: "draft" },
  { key: "collecting" },
  { key: "ingesting" },
  { key: "analyzing" },
  { key: "reporting" },
  { key: "sent" },
  { key: "approved" },
  { key: "archived" },
];

export function ProjectListPage() {
  return (
    <PreparationCabinetGate>
      <ScopedProjectListPage />
    </PreparationCabinetGate>
  );
}

function ScopedProjectListPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<ProjectRow["status"] | "all">("all");
  const pagination = usePagination();

  const { data, isLoading, isFetching, error, refetch } = useProjects({
    cursor: pagination.cursor,
    q: q || undefined,
    status: status === "all" ? undefined : status,
  });

  const onSearchChange = (next: string) => {
    setQ(next);
    pagination.reset();
  };
  const onStatusChange = (next: ProjectRow["status"] | "all") => {
    setStatus(next);
    pagination.reset();
  };
  const onResetFilters = () => {
    setQ("");
    setStatus("all");
    pagination.reset();
  };

  const hasFilters = q.trim() !== "" || status !== "all";
  const showSkeleton = isLoading && !data;
  const showEmpty = !!data && data.items.length === 0 && !hasFilters && !error;
  const showNoMatch = !!data && data.items.length === 0 && hasFilters && !error;

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      {/* Sticky page header — `top-16` mobile / `lg:top-0` desktop scroll-container. */}
      <header className="-mx-4 border-b border-slate-200 bg-white px-4 py-4 lg:-mx-8 lg:px-8 lg:py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              {t("projects.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{t("projects.subtitle")}</p>
          </div>
        </div>
      </header>

      <div className="px-0 py-6 lg:py-8">
        {/* Filters bar */}
        <div className="workspace-command-bar mb-6 flex flex-wrap items-center gap-3 py-2">
          <div className="min-w-0 flex-1 basis-64">
            <SearchInput
              value={q}
              onChange={onSearchChange}
              placeholder={t("projects.search_placeholder")}
            />
          </div>
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t("projects.filters.status")}</span>
            <select
              className="input"
              value={status}
              onChange={(e) => onStatusChange(e.target.value as ProjectRow["status"] | "all")}
            >
              {STATUSES.map((s) => (
                <option key={s.key} value={s.key}>
                  {t(`projects.statuses.${s.key}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Error retry */}
        {error && (
          <div
            role="alert"
            className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span>
              {error instanceof ApiException
                ? getApiErrorMessage(error, lang) || t("errors.generic")
                : t("errors.generic")}
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {showSkeleton && (
          <div
            aria-live="polite"
            className="space-y-2 rounded-xl border border-slate-200 bg-white p-4"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-md bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none"
              />
            ))}
          </div>
        )}

        {/* Empty actionable */}
        {showEmpty && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
            <h2 className="text-base font-semibold text-slate-900">
              {t("projects.empty_state.title")}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              {t("projects.empty_state.subtitle")}
            </p>
            <Link
              to="/requests/new"
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("nav.new_request")}
            </Link>
          </div>
        )}

        {/* No-match (filters active) */}
        {showNoMatch && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <strong className="block font-semibold">{t("projects.no_match.title")}</strong>
                <span className="text-amber-700">{t("projects.no_match.subtitle")}</span>
              </div>
              <button
                type="button"
                onClick={onResetFilters}
                className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              >
                {t("projects.no_match.reset")}
              </button>
            </div>
          </div>
        )}

        {/* Table desktop ≥ md */}
        {data && data.items.length > 0 && (
          <>
            <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    {/* 9.BG.1 — les colonnes disent ce que le dossier attend.
                        « Référence » et « Mis à jour » ont cédé la place : un
                        code interne et une date de modification n'aident à
                        choisir aucun dossier. La référence reste sous le titre. */}
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("projects.columns.client_and_file")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("projects.columns.operational_state")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("projects.columns.insurers")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      <span className="sr-only">{t("projects.columns.action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        {/* Le client d'abord : c'est par lui qu'on reconnaît
                            un dossier, jamais par son identifiant. */}
                        <span className="block text-xs text-slate-500">
                          {p.company_legal_name ?? "—"}
                        </span>
                        <Link
                          to={`/projects/${p.id}`}
                          className="text-brand-700 focus-visible:ring-brand-500 rounded-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                        >
                          {p.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {p.status === "archived" ? (
                          <ProjectStatusBadge status={p.status} />
                        ) : (
                          <EtatOperationnelBadge
                            lifecycleState={p.lifecycle_state}
                            etat={p.operational_state}
                            activityScope={p.activity_scope}
                            enRetard={p.is_late}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {p.recipients_prepared !== undefined && (
                          <p className="text-xs text-slate-600">
                            {t("flow_clarity.prepared_count", { count: p.recipients_prepared })}
                          </p>
                        )}
                        <EtatOperationnelDetail
                          historique={p.recipients_sent_historical}
                          sollicites={
                            p.recipients_sent_current ??
                            p.recipients_sent ??
                            (p.requests_sent === 0 ? 0 : p.insurers_solicited)
                          }
                          recues={p.responses_received}
                          attente={p.responses_awaiting}
                          echeance={p.next_due_at}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <EtatOperationnelAction
                          projectId={p.id}
                          etat={p.operational_state}
                          activityScope={p.activity_scope}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards stack < md */}
            <ul className="space-y-3 md:hidden">
              {data.items.map((p) => (
                <li key={p.id} className="rounded-xl border border-slate-200 bg-white p-4">
                  <Link
                    to={`/projects/${p.id}`}
                    className="text-brand-700 focus-visible:ring-brand-500 block rounded-sm text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  >
                    {p.title}
                  </Link>
                  {p.reference && <p className="mt-1 text-xs text-slate-500">{p.reference}</p>}
                  <p className="mt-1 break-words text-sm text-slate-600">
                    {p.company_legal_name ?? "—"}
                  </p>
                  <div className="mt-3 space-y-3">
                    {p.status === "archived" ? (
                      <ProjectStatusBadge status={p.status} />
                    ) : (
                      <EtatOperationnelBadge
                        lifecycleState={p.lifecycle_state}
                        etat={p.operational_state}
                        activityScope={p.activity_scope}
                        enRetard={p.is_late}
                      />
                    )}
                    <div>
                      {p.recipients_prepared !== undefined && (
                        <p className="text-xs text-slate-600">
                          {t("flow_clarity.prepared_count", { count: p.recipients_prepared })}
                        </p>
                      )}
                      <EtatOperationnelDetail
                        historique={p.recipients_sent_historical}
                        sollicites={
                          p.recipients_sent_current ??
                          p.recipients_sent ??
                          (p.requests_sent === 0 ? 0 : p.insurers_solicited)
                        }
                        recues={p.responses_received}
                        attente={p.responses_awaiting}
                        echeance={p.next_due_at}
                      />
                    </div>
                    <EtatOperationnelAction
                      projectId={p.id}
                      etat={p.operational_state}
                      activityScope={p.activity_scope}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <Pagination
                pageNumber={pagination.pageNumber}
                hasPrev={pagination.hasPrev}
                hasNext={!!data.next_cursor}
                onPrev={pagination.prev}
                onNext={() => pagination.next(data.next_cursor ?? null)}
                disabled={isFetching}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
